/**
 * Tiny string-safety helpers.
 *
 * Background: a recurring class of dashboard crashes is `TypeError: x.charAt
 * is not a function`. Optional chaining (`x?.charAt(0)`) only protects against
 * `null` / `undefined` — if `x` is a NUMBER (which several legacy MySQL columns
 * legitimately are), `.charAt` is `undefined` and the whole component tree
 * unmounts.
 *
 * Use `safeInitial(x)` anywhere you'd otherwise write `(x || '?').charAt(0)`
 * or `x?.charAt(0)?.toUpperCase()`. It coerces non-strings to a string first
 * and short-circuits on empty inputs.
 */

export function safeString(value: unknown, fallback: string = ''): string {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  // Numbers, bigints, booleans → toString. Avoid '[object Object]'.
  if (typeof value === 'number' || typeof value === 'bigint' || typeof value === 'boolean') {
    return String(value)
  }
  return fallback
}

export function safeInitial(value: unknown, fallback: string = '?'): string {
  const s = safeString(value, '')
  return s.length > 0 ? s.charAt(0).toUpperCase() : fallback
}
